/**
 * Lead capture: contact-form messages + app-waitlist signups.
 *
 * "2-Wege-Speicherung" requirement: every submission must (a) reach Brevo
 * (contact created/updated, so marketing automations/newsletters can act
 * on it) and (b) be persisted in Supabase (`contact_messages` /
 * `app_waitlist`, see supabase-schema.sql section 12) so Admin always has
 * a durable, queryable record regardless of whether the Brevo call
 * succeeds.
 *
 * Ordering: Supabase write happens FIRST and is the one that can fail the
 * user-visible submission (its RLS policies allow anonymous inserts by
 * design — see supabase-schema.sql section 12 — so this should basically
 * never fail for a well-formed submission). The Brevo sync happens SECOND,
 * via the `functions/api/brevo-sync.ts` Pages Function (never called
 * directly from the browser — BREVO_API_KEY must stay server-side, see
 * that function's header), and is deliberately best-effort: if it fails
 * (missing API key, Brevo outage, invalid list ID, etc.), the lead is
 * already safely stored in Supabase, so we swallow the Brevo error rather
 * than tell the user their message "failed to send" over a marketing-tool
 * hiccup. `brevo_synced` on the Supabase row records the outcome so Admin
 * can see (and, via retrySync below, manually retry) any leads that didn't
 * make it to Brevo.
 */
import { supabase } from "./supabase";

async function syncToBrevo(payload:
  | { kind: "contact"; name: string; email: string; message: string; rowId: string }
  | { kind: "app_waitlist"; email: string; rowId: string }
): Promise<boolean> {
  try {
    const res = await fetch("/api/brevo-sync", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { ok: boolean };
    return data.ok === true;
  } catch {
    // Network failure, function not deployed yet, etc. — never let a
    // Brevo-sync problem surface as a submission failure to the user (see
    // module header).
    return false;
  }
}

/**
 * Bug-class note (see src/lib/store.ts's createTicket() header for the
 * full explanation of the same underlying issue): `contact_messages` and
 * `app_waitlist`'s SELECT policies are staff/admin-only (supabase-schema.sql
 * section 12), so chaining `.select()` onto an anonymous visitor's INSERT
 * would hit PostgREST's RETURNING-respects-SELECT-RLS behavior and roll
 * back the whole insert with a 401, even though the INSERT policy itself
 * (`with check (true)`) allows it. Both functions below generate the row's
 * id client-side and insert with no `.select()` for exactly that reason.
 */
/**
 * Bug fix (found 2026-07-27 during live post-deploy verification): the
 * earlier version of this function tried to mark `brevo_synced = true`
 * itself, as a follow-up client-side `.update()` call after a successful
 * Brevo sync. That update always silently affected ZERO rows: anon/guest
 * submitters have no RLS UPDATE permission on `contact_messages`
 * (`contact_messages_update_staff` in supabase-schema.sql section 12 is
 * staff-only), and PostgREST returns HTTP 200 with an empty result set
 * when RLS hides every row from an UPDATE's visibility — not an error —
 * so the bug produced no exception to catch and went unnoticed until a
 * real production submission's `brevo_synced` column was checked
 * directly. Fixed by moving the flag-write into
 * `functions/api/brevo-sync.ts` itself, which already runs server-side
 * with a service_role key (see that file's `markSynced()` for the
 * corresponding half of this fix) — this function now only needs to pass
 * along the row id it just created.
 */
export async function submitContactMessage(input: { name: string; email: string; message: string }): Promise<void> {
  const id = crypto.randomUUID();
  const { error } = await supabase.from("contact_messages").insert({
    id,
    name: input.name,
    email: input.email,
    message: input.message,
    status: "Neu",
    brevo_synced: false,
  });
  if (error) throw error;

  await syncToBrevo({ kind: "contact", name: input.name, email: input.email, message: input.message, rowId: id });
}

/**
 * Bug fix (found 2026-07-27 during live post-deploy verification): this
 * previously used `.upsert(..., { onConflict: "email" })`, which compiles
 * to PostgREST's `Prefer: resolution=merge-duplicates` — confirmed live
 * that this fails with the SAME RLS-visibility problem as an UPDATE
 * (`app_waitlist` has no UPDATE policy for anon at all, only the staff-
 * only SELECT from supabase-schema.sql section 12), even when there is no
 * actual conflicting row yet (a brand-new email still hit
 * "new row violates row-level security policy" once `on_conflict`/
 * `resolution=ignore-duplicates` was in play, because Postgres's
 * `ON CONFLICT` clause needs to evaluate row visibility to determine
 * whether a conflict exists at all).
 *
 * Fixed by doing a plain INSERT (no upsert/on_conflict semantics at all)
 * and specifically catching Postgres's unique-violation error code
 * `23505` (`app_waitlist_email_key`), which fires cleanly as a distinct,
 * recognizable error from an unrelated RLS `42501` failure — verified
 * live against production. A duplicate-email resubmission is then simply
 * treated as "already on the waitlist", which is the correct UX (no
 * error shown to the user), without needing any RLS/UPDATE permission
 * change on the table.
 */
export async function joinAppWaitlist(input: { email: string }): Promise<void> {
  const id = crypto.randomUUID();
  const { error } = await supabase.from("app_waitlist").insert({ id, email: input.email, brevo_synced: false });

  if (error) {
    const alreadyOnWaitlist = error.code === "23505";
    if (!alreadyOnWaitlist) throw error;
    // Already registered — nothing more to do; don't re-sync to Brevo
    // (they were already synced on their first submission) and don't
    // surface this as an error to the user.
    return;
  }

  await syncToBrevo({ kind: "app_waitlist", email: input.email, rowId: id });
}

export type ContactMessage = {
  id: string;
  name: string;
  email: string;
  message: string;
  status: "Neu" | "Bearbeitet";
  brevo_synced: boolean;
  created_at: string;
};

export type AppWaitlistEntry = {
  id: string;
  email: string;
  brevo_synced: boolean;
  created_at: string;
};

/** Staff/Admin-only reads (RLS-enforced — see supabase-schema.sql section 12), for the Admin dashboard's new "Leads" tab. */
export async function getContactMessages(): Promise<ContactMessage[]> {
  const { data, error } = await supabase
    .from("contact_messages")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ContactMessage[];
}

export async function getAppWaitlist(): Promise<AppWaitlistEntry[]> {
  const { data, error } = await supabase
    .from("app_waitlist")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as AppWaitlistEntry[];
}

export async function setContactMessageStatus(id: string, status: ContactMessage["status"]): Promise<void> {
  const { error } = await supabase.from("contact_messages").update({ status }).eq("id", id);
  if (error) throw error;
}
